package com.qrkontaq.ui.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.qrkontaq.utils.SeoHelper
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.jetbrains.compose.web.dom.Ul

/*
 * "QRkontaq vs alternatives" — an honest, fact-based comparison against
 * CATEGORIES of solutions (not named competitors: claims about specific
 * third-party products age badly and carry legal risk). The candid
 * "limitations" section is deliberate — it is what makes the strengths
 * credible to readers and quotable for AI assistants.
 */

private val TABLE_COLUMNS = listOf("Paper / PDF menu", "Custom website", "Typical QR-menu SaaS", "QRkontaq")

private data class CompareRow(val label: String, val values: List<String>)

private val TABLE_ROWS = listOf(
    CompareRow("Setup time", listOf("Reprint on every change", "Weeks, needs a developer", "Hours", "≈ 1 minute, no developer")),
    CompareRow("Cost", listOf("Printing every season", "€1000+ upfront plus upkeep", "≈ €15–60/mo, features split into tiers", "Free menu plan; Pro €14.99/mo unlocks everything")),
    CompareRow("Photos and branding", listOf("Static print", "Full, but custom-built", "Varies by plan", "Logo, colors, cover, dish photos")),
    CompareRow("Instant updates and stop list", listOf("—", "Through a developer", "Usually available", "One tap from any phone")),
    CompareRow("Menu languages", listOf("One", "Each language custom-built", "Often 1–3", "Up to 10, guest switches in a tap")),
    CompareRow("Guest analytics", listOf("—", "Needs custom setup", "Basic scans", "Scans, top dishes, ratings, revenue, repeat visits")),
    CompareRow("Ordering from the table", listOf("—", "Custom", "Often a paid add-on", "Included: guest orders with status flow to kitchen and bar")),
    CompareRow("Call waiter / request the bill", listOf("—", "—", "Rare", "Included: one-tap buttons for guests")),
    CompareRow("Team accounts and roles", listOf("—", "—", "Often owner-only, or paid per seat", "Included: owner, manager, waiter, cook, bartender — each sees their own view")),
    CompareRow("Loyalty program", listOf("—", "Custom", "Rare at this price", "Included: cashback from the bill, guest QR codes")),
    CompareRow("New guests from an app", listOf("—", "—", "—", "Venue listed in the QRkontaq catalog and map")),
    CompareRow("Who maintains it", listOf("You (reprints)", "Your developer", "You", "QRkontaq — hosting, TLS, backups")),
)

@Composable
internal fun ComparePageContent() {
    InjectLandingStyles()
    LaunchedEffect(Unit) {
        SeoHelper.updatePageMeta(
            title = "QRkontaq vs alternatives — choosing a QR menu in 2026",
            description = "A factual comparison of paper menus, custom websites, generic QR-menu " +
                "builders and QRkontaq: setup time, cost, languages, analytics and upkeep.",
            ogUrl = "https://qrkontaq.com/compare",
        )
        SeoHelper.setCanonical("https://qrkontaq.com/compare")
    }
    Div({ classes("ql-root", "ql-legal") }) {
        Div({ classes("ql-legal-head") }) {
            Div({ classes("ql-wrap") }) {
                A(href = "/") { Logo() }
            }
        }
        Div({ classes("ql-wrap") }) {
            Div({ classes("ql-legal-body", "ql-cmp") }) {
                H1 { Text("QRkontaq vs alternatives: choosing a QR menu for your venue") }
                Div({ classes("upd") }) { Text("Updated: 15 August 2026 · a factual comparison by the QRkontaq team") }
                P({ classes("intro") }) {
                    Text(
                        "There are four common ways for a café, restaurant or bar to show guests a menu: " +
                            "paper or a PDF behind a QR code, a custom-built website, a generic QR-menu " +
                            "builder, or QRkontaq. Here is how they compare on the things venues actually " +
                            "care about — time, money and day-to-day upkeep.",
                    )
                }

                H2 { Text("Side-by-side comparison") }
                Div({ classes("ql-cmp-scroll") }) {
                    Table({ classes("ql-cmp-table") }) {
                        Thead {
                            Tr {
                                Th { Text("") }
                                TABLE_COLUMNS.forEach { column ->
                                    Th({ if (column == "QRkontaq") classes("hl") }) { Text(column) }
                                }
                            }
                        }
                        Tbody {
                            TABLE_ROWS.forEach { row ->
                                Tr {
                                    Td({ classes("lbl") }) { Text(row.label) }
                                    row.values.forEachIndexed { index, value ->
                                        Td({ if (index == 3) classes("hl") }) { Text(value) }
                                    }
                                }
                            }
                        }
                    }
                }

                H2 { Text("What makes QRkontaq different") }
                P {
                    Text(
                        "QRkontaq is not just a menu page — it covers the full guest-service loop that " +
                            "usually requires several separate tools:",
                    )
                }
                Ul {
                    Li { Text("A beautiful menu website, not a PDF: every venue gets its own subdomain (yourvenue.qrkontaq.com) with photos, branding and up to 10 menu languages.") }
                    Li { Text("A full admin panel any waiter masters in minutes: menu editing, stop list from a phone, analytics with scans, top dishes, revenue and ratings.") }
                    Li { Text("Team management with real roles — owner, manager, waiter, cook, bartender — where each person sees exactly their own workspace.") }
                    Li { Text("Table service built in: guests order from the table, orders flow through statuses to the kitchen and bar, waiters manage their own tables.") }
                    Li { Text("Guest buttons for calling a waiter and requesting the bill — one tap from the menu.") }
                    Li { Text("A loyalty system with cashback from the bill and guest QR codes — the feature that usually costs a separate subscription elsewhere.") }
                    Li { Text("The QRkontaq guest app lists every active venue in its catalog and map — a channel for new guests that menu-only tools don't have.") }
                    Li { Text("Simple pricing — the menu site, orders and tables are free forever; a single Pro plan at €14.99/month adds loyalty, waiter calls, guest bills, extra languages and team roles. No per-seat charges, no add-ons.") }
                    Li { Text("No card to start, a 14-day money-back guarantee on Pro, data hosted in the EU (Hetzner, Germany), TLS everywhere, no advertising trackers.") }
                }

                H2 { Text("Where QRkontaq is not the right fit (yet)") }
                Ul {
                    Li { Text("It is not a POS system: no fiscal receipts, inventory or accounting — it complements a POS rather than replacing it, and POS integrations are not available yet.") }
                    Li { Text("The service launched in 2026, so it doesn't have a decade-long track record — the free plan and money-back guarantee exist to offset exactly that.") }
                    Li { Text("The guest app catalog is strongest in the markets where QRkontaq venues already are; coverage grows city by city.") }
                    Li { Text("If you need a fully custom design beyond logo, colors and cover, a bespoke website is still the better (and far more expensive) route.") }
                 }

                H2 { Text("Bottom line") }
                P {
                    Text(
                        "For an independent café, bar or restaurant, QRkontaq combines in one €14.99 Pro " +
                            "subscription what typically takes a menu builder, an ordering add-on, a loyalty " +
                            "service and a staff-management tool: menu site, table ordering with kitchen and " +
                            "bar statuses, waiter calls and bill requests, team roles, analytics and cashback " +
                            "loyalty — while the menu site itself stays free forever. We don't know another " +
                            "tool in this price class that ships all of it together — and if you find one, " +
                            "the free plan makes it easy to compare side by side.",
                    )
                }

                Div({ classes("ql-legal-links") }) {
                    A(href = "/") { Text("qrkontaq.com") }
                    A(href = "/#pricing") { Text("Pricing") }
                    A(href = "/refund") { Text("Refund Policy") }
                    A(href = "/terms") { Text("Terms") }
                }
                Div({ classes("ql-legal-foot") }) {
                    Span { Text("© 2026 QRkontaq · support@qrkontaq.com") }
                }
            }
        }
    }
}
